/**
 * SignalsHub — unified inbox for peer signals.
 *
 * Combines `/feedback` (360 peer feedback) and `/recognition` (kudos) into
 * one navigation slot under the "Me" section. Tab is driven by ?tab=
 * so legacy links from emails or notifications still land users on the
 * exact pane they expected.
 *
 * Each pane keeps its own header to preserve existing UX; the hub renders
 * only the tab strip on top.
 */
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageSquareQuote, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import Feedback360 from "@/pages/Feedback360";
import Recognition from "@/pages/Recognition";

const TABS = [
  { id: "feedback", label: "Feedback", icon: MessageSquareQuote },
  { id: "kudos", label: "Kudos", icon: Sparkles },
];

export default function SignalsHub() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const requested = params.get("tab") || "feedback";
  const active = TABS.find((t) => t.id === requested) ? requested : "feedback";

  const setTab = (id) => {
    const next = new URLSearchParams(location.search);
    if (id === "feedback") next.delete("tab");
    else next.set("tab", id);
    navigate({ pathname: "/signals", search: next.toString() ? `?${next.toString()}` : "" }, { replace: true });
  };

  return (
    <div className="space-y-5" data-testid="signals-hub">
      <nav className="flex items-center gap-1 border-b border-slate-200" data-testid="signals-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = t.id === active;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`signals-tab-${t.id}`}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition -mb-[2px]",
                isActive
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-900",
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </nav>

      <div data-testid={`signals-pane-${active}`}>
        {active === "feedback" && <Feedback360 />}
        {active === "kudos" && <Recognition />}
      </div>
    </div>
  );
}
